// Collar / Frame nesting + cut-time math.
//
// Ported verbatim from v1 (ai-quote/index.html ~14208 + ~14390). v1's
// `_calcCollarNesting` and `_calcCollarCutTime` were the meat of the
// "Collar" part type — a picture-frame rectangle cut from G-10 / GPO-3 /
// G-11 phenolic sheet with a nesting calculator and a cut-time estimator.
// v2 lost the type during the rewrite; this module brings the math back
// so the new CollarNestingStep + CollarCutStep can reuse it.
//
// Both functions are pure — no React, no state, no DOM. The wizard steps
// re-invoke them on every input change to drive a live readout.

/** Coerce anything to a finite number; non-numeric / NaN → 0. Matches v1's
 *  `num()` helper so the ported math behaves identically on stringy inputs. */
function num(v: unknown): number {
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

export interface CollarNestingResult {
  /** Columns of parts along the sheet's length axis. */
  cols: number;
  /** Rows of parts along the sheet's width axis. */
  rows: number;
  /** Total parts per sheet = cols × rows. */
  count: number;
  /** True when orientation B (part rotated 90°) yields more parts than A. */
  rotated: boolean;
  /** Effective part length used in the SVG layout (orientation-dependent). */
  partLen: number;
  /** Effective part width used in the SVG layout (orientation-dependent). */
  partWid: number;
}

/**
 * Shelf-pack nesting for a single rectangular collar on a rectangular sheet.
 *
 * v1 picks the better of two orientations:
 *   A. Part's long side along sheet length
 *   B. Part rotated 90° (long side along sheet width)
 *
 * Why two orientations? On long-thin parts (30 × 10) on a 96 × 48 sheet,
 * rotation can swing parts/sheet from 12 → 9 (or the other way around).
 * The shelf-pack assumes a uniform grid — no mixed-orientation packing.
 *
 * `spacing` is the gap added between adjacent parts (v1 called this `kerf`).
 * Added once to the denominator and once to the numerator to handle the
 * fact that the LAST part doesn't need a trailing gap.
 *
 * @param outsideLen  Outer rectangle length, inches.
 * @param outsideWid  Outer rectangle width, inches.
 * @param sheetLen    Sheet length, inches.
 * @param sheetWid    Sheet width, inches.
 * @param spacing     Gap between adjacent parts, inches (0 = parts touch).
 */
export function calcCollarNesting(
  outsideLen: number,
  outsideWid: number,
  sheetLen: number,
  sheetWid: number,
  spacing: number,
): CollarNestingResult {
  const oLen = Math.max(0.001, num(outsideLen));
  const oWid = Math.max(0.001, num(outsideWid));
  const sLen = Math.max(0.001, num(sheetLen));
  const sWid = Math.max(0.001, num(sheetWid));
  const kerf = Math.max(0, num(spacing));

  const oL = oLen + kerf;
  const oW = oWid + kerf;

  // Orientation A: long side along sheet length
  const colsA = Math.floor((sLen + kerf) / oL);
  const rowsA = Math.floor((sWid + kerf) / oW);
  const nA = Math.max(0, colsA) * Math.max(0, rowsA);

  // Orientation B: rotated 90°
  const colsB = Math.floor((sLen + kerf) / oW);
  const rowsB = Math.floor((sWid + kerf) / oL);
  const nB = Math.max(0, colsB) * Math.max(0, rowsB);

  if (nB > nA) {
    return {
      cols: colsB,
      rows: rowsB,
      count: nB,
      rotated: true,
      partLen: oWid,
      partWid: oLen,
    };
  }
  return {
    cols: colsA,
    rows: rowsA,
    count: nA,
    rotated: false,
    partLen: oLen,
    partWid: oWid,
  };
}

export interface CollarCutTimeResult {
  /** Material thickness ÷ DOC, rounded up. Min 1 pass even on zero/neg DOC. */
  passes: number;
  /** Echo of the feed rate used (IPM). */
  feedIpm: number;
  /** Echo of the depth-of-cut used (in). */
  doc: number;
  /** Perimeter of the inner cutout (in). */
  innerPerimeter: number;
  /** Perimeter of the outer rectangle (in). */
  outerPerimeter: number;
  /** Cut time for the inside cutout, per part (minutes). */
  cutInsidePerPart: number;
  /** Cut time for the outside profile, per part (minutes). */
  cutOutsidePerPart: number;
  /** Inside + outside cut time per part (minutes). */
  cutCombinedPerPart: number;
}

/**
 * Cut-time estimator for one collar.
 *
 * Model: a picture frame = outer rectangle (outsideLen × outsideWid) with an
 * inner cutout offset by `frameWidth` on all four sides. Cut time for each
 * perimeter is `(perimeter × passes) / feedIpm` where `passes = ceil(thk/doc)`.
 *
 * This is intentionally simple — no lead-ins, no corner slowdowns, no
 * acceleration limits. Matches v1's quick-quote intent: estimate to ±15%
 * so a price falls out fast.
 *
 * All inputs in inches; feedIpm in inches/minute; result times in minutes.
 */
export function calcCollarCutTime(input: {
  outsideLen: number;
  outsideWid: number;
  frameWidth: number;
  feedIpm: number;
  doc: number;
  materialThickness: number;
}): CollarCutTimeResult {
  const oL = num(input.outsideLen);
  const oW = num(input.outsideWid);
  const fw = num(input.frameWidth);
  const feedIpm = num(input.feedIpm);
  const doc = num(input.doc);
  const matThk = num(input.materialThickness);

  const passes = doc > 0 ? Math.max(1, Math.ceil(matThk / doc)) : 1;

  // Inside cutout = outside − 2 × frame width on each side
  const innerL = Math.max(0, oL - 2 * fw);
  const innerW = Math.max(0, oW - 2 * fw);
  const outerPerimeter = 2 * (oL + oW);
  const innerPerimeter = 2 * (innerL + innerW);

  const cutInsidePerPart = feedIpm > 0 ? (innerPerimeter * passes) / feedIpm : 0;
  const cutOutsidePerPart = feedIpm > 0 ? (outerPerimeter * passes) / feedIpm : 0;

  return {
    passes,
    feedIpm,
    doc,
    innerPerimeter,
    outerPerimeter,
    cutInsidePerPart,
    cutOutsidePerPart,
    cutCombinedPerPart: cutInsidePerPart + cutOutsidePerPart,
  };
}

/**
 * KMF-typical defaults for collar work. Seeded from v1's
 * `settings.collarDefaults`. A future Settings page will let Justin override
 * these per-shop; until then they're baked in.
 */
export const COLLAR_DEFAULTS = {
  /** Feed rate for the router on phenolic, IPM. */
  feedIpm: 50,
  /** Depth of cut per pass, inches. */
  doc: 0.25,
  /** Inter-part spacing on the nest, inches. v1 called this `kerf`. */
  spacing: 0.05,
  /** Standard sheet stock length (long axis), inches. */
  sheetLen: 96,
  /** Standard sheet stock width (short axis), inches. */
  sheetWid: 48,
  /** Machine + operator burdened rate, $/hr. */
  hourlyRate: 85,
  /** Time to load + unload a sheet on the router, minutes. */
  loadUnloadMin: 5,
  /** Time to screw the sheet down to the table, minutes. */
  screwDownMin: 3,
  /** Common sheet-size presets shown as one-click chips on the wizard.
   *  Stored sorted small → large so the chip row reads naturally. The labels
   *  use × (Unicode times) consistent with the rest of the shop UI. */
  sheetPresets: [
    { label: '6×6', len: 6, wid: 6 },
    { label: '6×12', len: 12, wid: 6 },
    { label: '12×12', len: 12, wid: 12 },
    { label: '6×18', len: 18, wid: 6 },
    { label: '12×18', len: 18, wid: 12 },
    { label: '18×18', len: 18, wid: 18 },
    { label: '24×24', len: 24, wid: 24 },
    { label: '24×48', len: 48, wid: 24 },
    { label: '36×36', len: 36, wid: 36 },
    { label: '48×48', len: 48, wid: 48 },
    { label: '4×8', len: 96, wid: 48 },
    { label: '5×10', len: 120, wid: 60 },
  ],
} as const;
